import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Tooltip,
  Alert,
  CircularProgress,
  Link,
  Paper,
  Tab,
  Tabs,
} from '@mui/material';
import {
  FileText,
  Plus,
  Trash2,
  ExternalLink,
  Cloud,
  HardDrive,
  Link2,
  File,
  Image,
  FileSpreadsheet,
  Presentation,
  FileCode,
  FileArchive,
  Video,
  Music,
  Folder,
} from 'lucide-react';
import { Link as RouterLink } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { Task, LinkedDocument, DocumentProvider } from '@/types';
import { AppSelect, AppMenuItem } from '@/shared/components/AppSelect';
import ConfirmationModal from '@/shared/components/modals/ConfirmationModal';
import { documentService, type LibraryDocument } from '@/services/api/documentService';
import { QUERY_KEYS } from '@/services/queryClient';
import { FileTypeIcon, flattenCategoryTree } from '@/features/documents/documentUi';

dayjs.extend(relativeTime);

interface TaskDocumentsTabProps {
  task: Task;
  onAddDocument?: (document: Omit<LinkedDocument, 'id' | 'linkedAt' | 'linkedBy'>) => Promise<void>;
  onRemoveDocument?: (documentId: string) => Promise<void>;
  isLoading?: boolean;
  canEdit?: boolean;
}

interface AddDocumentModalProps {
  open: boolean;
  onClose: () => void;
  onAdd: (document: Omit<LinkedDocument, 'id' | 'linkedAt' | 'linkedBy'>) => Promise<void>;
  existingDocuments: LinkedDocument[];
}

function inferProviderFromUrl(url: string): DocumentProvider {
  const u = url.toLowerCase();
  if (
    u.includes('drive.google.com')
    || u.includes('docs.google.com')
    || u.includes('sheets.google.com')
    || u.includes('slides.google.com')
  ) {
    return 'google_drive';
  }
  if (u.includes('sharepoint.com')) return 'sharepoint';
  if (u.includes('onedrive.live.com') || u.includes('onedrive.com') || u.includes('1drv.ms')) {
    return 'onedrive';
  }
  return 'manual';
}

const AddDocumentModal: React.FC<AddDocumentModalProps> = ({ open, onClose, onAdd, existingDocuments }) => {
  const [tab, setTab] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [folderId, setFolderId] = useState('');
  const [selectedLibraryId, setSelectedLibraryId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    url: '',
    provider: 'manual' as DocumentProvider,
  });

  const categoriesQuery = useQuery({
    queryKey: QUERY_KEYS.documentCategories(false),
    queryFn: () => documentService.listCategories(false),
    enabled: open,
  });
  // Only folders this user can open (backend ACL + skip path-only parents).
  const folders = useMemo(
    () =>
      (categoriesQuery.data || []).filter(
        (c) => c.isActive && !c.pathOnly && (c.myRole === 'viewer' || c.myRole === 'editor')
      ),
    [categoriesQuery.data]
  );
  const folderTree = useMemo(() => flattenCategoryTree(folders), [folders]);
  const activeFolderId = folderId && folders.some((f) => f.id === folderId) ? folderId : (folders[0]?.id ?? '');

  const filesQuery = useQuery({
    queryKey: QUERY_KEYS.documents(activeFolderId),
    queryFn: () => documentService.listDocuments(activeFolderId),
    enabled: open && tab === 0 && Boolean(activeFolderId),
  });
  const libraryFiles = filesQuery.data || [];
  const selectedFile = libraryFiles.find((f) => f.id === selectedLibraryId) || null;

  const alreadyLinked = (doc: LibraryDocument) =>
    existingDocuments.some(
      (linked) =>
        linked.url === doc.webViewUrl
        || (doc.driveFileId && linked.externalId === doc.driveFileId)
    );

  const handleUrlChange = (url: string) => {
    setFormData((prev) => ({
      ...prev,
      url,
      provider: url.trim() ? inferProviderFromUrl(url) : 'manual',
    }));
  };

  const linkPayload = async (payload: Omit<LinkedDocument, 'id' | 'linkedAt' | 'linkedBy'>) => {
    setIsSubmitting(true);
    setError('');
    try {
      await onAdd(payload);
      setFormData({ name: '', url: '', provider: 'manual' });
      setSelectedLibraryId(null);
      onClose();
    } catch (err) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message
        || (err instanceof Error ? err.message : 'Failed to add document');
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePasteSubmit = async () => {
    if (!formData.name.trim() || !formData.url.trim()) return;
    await linkPayload({
      name: formData.name.trim(),
      url: formData.url.trim(),
      provider: formData.provider,
    });
  };

  const handleLibrarySubmit = async () => {
    if (!selectedFile) return;
    if (alreadyLinked(selectedFile)) {
      setError('This file is already linked to the task.');
      return;
    }
    await linkPayload({
      name: selectedFile.name,
      url: selectedFile.webViewUrl,
      provider: 'google_drive',
      mimeType: selectedFile.mimeType,
      size: selectedFile.size,
      externalId: selectedFile.driveFileId || undefined,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Link Document</DialogTitle>
      <DialogContent>
        <Tabs value={tab} onChange={(_, value) => { setTab(value); setError(''); }} sx={{ mb: 2 }}>
          <Tab label="From library" />
          <Tab label="Paste link" />
        </Tabs>

        {tab === 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {categoriesQuery.isLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={24} />
              </Box>
            ) : folders.length === 0 ? (
              <Alert severity="info">
                No folders you can access.{' '}
                <Link component={RouterLink} to="/documents">Open Documents</Link>
                {' '}or ask an admin to share a folder with you.
              </Alert>
            ) : (
              <>
                <AppSelect
                  label="Folder"
                  value={activeFolderId}
                  onChange={(e) => {
                    setFolderId(String(e.target.value));
                    setSelectedLibraryId(null);
                  }}
                >
                  {folderTree.map(({ cat, depth }) => (
                    <AppMenuItem key={cat.id} value={cat.id}>
                      {`${'— '.repeat(depth)}${cat.name}`}
                    </AppMenuItem>
                  ))}
                </AppSelect>
                {filesQuery.isLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : libraryFiles.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No files in this folder. Add them on the{' '}
                    <Link component={RouterLink} to="/documents">Documents</Link> page.
                  </Typography>
                ) : (
                  <List disablePadding sx={{ maxHeight: 280, overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                    {libraryFiles.map((doc) => {
                      const linked = alreadyLinked(doc);
                      return (
                        <ListItemButton
                          key={doc.id}
                          selected={selectedLibraryId === doc.id}
                          disabled={linked}
                          onClick={() => setSelectedLibraryId(doc.id)}
                        >
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <FileTypeIcon doc={doc} size={18} />
                          </ListItemIcon>
                          <ListItemText
                            primary={doc.name}
                            secondary={linked ? 'Already on this task' : undefined}
                            primaryTypographyProps={{ noWrap: true, fontSize: '0.875rem' }}
                          />
                        </ListItemButton>
                      );
                    })}
                  </List>
                )}
              </>
            )}
            {error && <Alert severity="error">{error}</Alert>}
          </Box>
        )}

        {tab === 1 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Document Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Requirements Specification"
              fullWidth
              required
            />
            <TextField
              label="Link"
              value={formData.url}
              onChange={(e) => handleUrlChange(e.target.value)}
              placeholder="https://drive.google.com/file/d/..."
              fullWidth
              required
              helperText="Paste a Google Drive, OneDrive, SharePoint, or any https link"
            />
            <AppSelect
              label="Provider"
              value={formData.provider}
              onChange={(e) => setFormData({ ...formData, provider: e.target.value as DocumentProvider })}
            >
              <AppMenuItem value="manual">Other link</AppMenuItem>
              <AppMenuItem value="google_drive">Google Drive</AppMenuItem>
              <AppMenuItem value="onedrive">OneDrive</AppMenuItem>
              <AppMenuItem value="sharepoint">SharePoint</AppMenuItem>
            </AppSelect>
            <Alert severity="info">
              <Typography variant="body2">
                Prefer files from the company library when you can. Paste a link only for something that is not in Documents.
              </Typography>
            </Alert>
            {error && <Alert severity="error">{error}</Alert>}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        {tab === 0 ? (
          <Button
            variant="contained"
            onClick={handleLibrarySubmit}
            disabled={!selectedFile || isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={16} /> : <Folder size={16} />}
          >
            Link from library
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handlePasteSubmit}
            disabled={!formData.name.trim() || !formData.url.trim() || isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={16} /> : <Plus size={16} />}
          >
            Link Document
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

const getDocumentIcon = (mimeType?: string, name?: string) => {
  const extension = name?.split('.').pop()?.toLowerCase();

  if (mimeType?.includes('image') || ['png', 'jpg', 'jpeg', 'gif', 'svg', 'webp'].includes(extension || '')) {
    return <Image size={20} style={{ color: '#10b981' }} />;
  }
  if (mimeType?.includes('spreadsheet') || ['xlsx', 'xls', 'csv'].includes(extension || '')) {
    return <FileSpreadsheet size={20} style={{ color: '#22c55e' }} />;
  }
  if (mimeType?.includes('presentation') || ['pptx', 'ppt'].includes(extension || '')) {
    return <Presentation size={20} style={{ color: '#f97316' }} />;
  }
  if (mimeType?.includes('video') || ['mp4', 'avi', 'mov', 'mkv'].includes(extension || '')) {
    return <Video size={20} style={{ color: '#8b5cf6' }} />;
  }
  if (mimeType?.includes('audio') || ['mp3', 'wav', 'flac'].includes(extension || '')) {
    return <Music size={20} style={{ color: '#ec4899' }} />;
  }
  if (['zip', 'rar', '7z', 'tar', 'gz'].includes(extension || '')) {
    return <FileArchive size={20} style={{ color: '#78716c' }} />;
  }
  if (['js', 'ts', 'tsx', 'jsx', 'py', 'java', 'cpp', 'c', 'html', 'css', 'json'].includes(extension || '')) {
    return <FileCode size={20} style={{ color: '#4F46E5' }} />;
  }
  if (mimeType?.includes('pdf') || extension === 'pdf') {
    return <FileText size={20} style={{ color: '#ef4444' }} />;
  }
  if (mimeType?.includes('document') || ['doc', 'docx', 'txt', 'md'].includes(extension || '')) {
    return <FileText size={20} style={{ color: '#4F46E5' }} />;
  }
  return <File size={20} style={{ color: '#64748b' }} />;
};

const getProviderInfo = (provider: DocumentProvider) => {
  switch (provider) {
    case 'google_drive':
      return {
        icon: <Cloud size={14} />,
        label: 'Google Drive',
        color: '#4285f4',
        bgColor: '#e8f0fe',
      };
    case 'onedrive':
      return {
        icon: <Cloud size={14} />,
        label: 'OneDrive',
        color: '#0078d4',
        bgColor: '#e6f2ff',
      };
    case 'sharepoint':
      return {
        icon: <HardDrive size={14} />,
        label: 'SharePoint',
        color: '#036c70',
        bgColor: '#e6f5f5',
      };
    case 'manual':
    default:
      return {
        icon: <Link2 size={14} />,
        label: 'Link',
        color: '#64748b',
        bgColor: '#f1f5f9',
      };
  }
};

/**
 * TaskDocumentsTab
 * Paste Google Drive / OneDrive / SharePoint / other URLs onto a task.
 */
const TaskDocumentsTab: React.FC<TaskDocumentsTabProps> = ({
  task,
  onAddDocument,
  onRemoveDocument,
  isLoading,
  canEdit,
}) => {
  const editable = canEdit ?? true;
  const [showAddModal, setShowAddModal] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [pendingRemove, setPendingRemove] = useState<LinkedDocument | null>(null);

  const documents = task.linkedDocuments || [];

  const handleRemoveDocument = async (documentId: string) => {
    if (!onRemoveDocument || !editable) return;
    setRemovingId(documentId);
    try {
      await onRemoveDocument(documentId);
    } finally {
      setRemovingId(null);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle2" color="text.secondary">
          {documents.length} document{documents.length !== 1 ? 's' : ''} linked
        </Typography>
        {editable && onAddDocument && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<Plus size={16} />}
            onClick={() => setShowAddModal(true)}
          >
            Link Document
          </Button>
        )}
      </Box>

      {documents.length === 0 ? (
        <Paper
          sx={{
            py: 6,
            px: 3,
            textAlign: 'center',
            bgcolor: 'grey.50',
            border: '1px dashed',
            borderColor: 'grey.300',
          }}
        >
          <FileText size={48} style={{ color: '#94a3b8', marginBottom: 16 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No Documents Linked
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Attach a file from the Documents library, or paste any Drive / https link
          </Typography>
          {editable && onAddDocument && (
            <Button
              variant="contained"
              startIcon={<Plus size={16} />}
              onClick={() => setShowAddModal(true)}
            >
              Link Your First Document
            </Button>
          )}
        </Paper>
      ) : (
        <List disablePadding>
          {documents.map((doc) => {
            const providerInfo = getProviderInfo(doc.provider);
            const isRemoving = removingId === doc.id;

            return (
              <ListItem
                key={doc.id}
                sx={{
                  px: 2,
                  py: 1.5,
                  borderRadius: 1,
                  mb: 1,
                  bgcolor: 'grey.50',
                  '&:hover': { bgcolor: 'grey.100' },
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  {getDocumentIcon(doc.mimeType, doc.name)}
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Link
                        href={doc.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          textDecoration: 'none',
                          color: 'text.primary',
                          fontWeight: 500,
                          '&:hover': { color: 'primary.main', textDecoration: 'underline' },
                        }}
                      >
                        {doc.name}
                      </Link>
                      <Chip
                        icon={providerInfo.icon}
                        label={providerInfo.label}
                        size="small"
                        sx={{
                          height: 22,
                          fontSize: '0.7rem',
                          bgcolor: providerInfo.bgColor,
                          color: providerInfo.color,
                          '& .MuiChip-icon': { color: providerInfo.color },
                        }}
                      />
                    </Box>
                  }
                  secondary={
                    <Typography variant="caption" color="text.secondary">
                      Linked {dayjs(doc.linkedAt).fromNow()}
                      {doc.size ? ` • ${(doc.size / 1024 / 1024).toFixed(2)} MB` : ''}
                    </Typography>
                  }
                />
                <ListItemSecondaryAction>
                  <Tooltip title="Open in new tab">
                    <IconButton
                      size="small"
                      onClick={() => window.open(doc.url, '_blank')}
                      sx={{ mr: 0.5 }}
                    >
                      <ExternalLink size={16} />
                    </IconButton>
                  </Tooltip>
                  {editable && onRemoveDocument && (
                    <Tooltip title="Remove link">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => setPendingRemove(doc)}
                        disabled={isRemoving}
                      >
                        {isRemoving ? <CircularProgress size={16} /> : <Trash2 size={16} />}
                      </IconButton>
                    </Tooltip>
                  )}
                </ListItemSecondaryAction>
              </ListItem>
            );
          })}
        </List>
      )}

      {editable && onAddDocument && (
        <AddDocumentModal
          open={showAddModal}
          onClose={() => setShowAddModal(false)}
          onAdd={onAddDocument}
          existingDocuments={documents}
        />
      )}

      <ConfirmationModal
        open={!!pendingRemove}
        onClose={() => setPendingRemove(null)}
        data={
          pendingRemove
            ? {
                title: 'Remove document',
                message: `Remove “${pendingRemove.name}” from this task? The file itself is not deleted.`,
                confirmText: 'Remove',
                type: 'danger',
                onConfirm: () => {
                  handleRemoveDocument(pendingRemove.id);
                  setPendingRemove(null);
                },
              }
            : undefined
        }
      />
    </Box>
  );
};

export default TaskDocumentsTab;
